<template>
  <div>
    <div v-if="!showSettings">
      <h4>
        <strong>Warning:</strong> These settings are used for printing via File Drop to a PrintMate
        printer. If your lab does not do this, please disregard these settings, as they may cause
        issues in the app.
      </h4>
      <button class="btn btn-primary mb-2" @click.prevent="enableShowSettings">Configure</button>
    </div>
    <div v-else-if="shouldDisable">
      <h4 class="text-danger">
        File Drop printing has been disabled. Once you click Save, all previous settings will be
        lost.
      </h4>
      <button class="btn btn-success" @click="shouldDisable = false">Undo</button>
    </div>
    <div v-else class="mb-2">
      <text-input v-model="settings.url" label="URL" />
      <div class="border p-2 mt-2">
        <h3>Cassettes</h3>
        <text-input v-model="settings.printerName" label="Cassette Printer Name" />
        <div v-show="settings.printerName">
          <select-input
            v-model="settings.atAccessioning"
            :items="booleanOptions"
            label="Print Cassettes at Accessioning"
          />
          <text-input
            v-model="trayOptions"
            label="Cassette Tray Options"
            @change="setDefaultTray"
          />
          <select-input
            v-model="settings.defaultTray"
            :items="defaultTrayOptions"
            label="Default Tray"
            valueExpr="displayName"
          />
          <text-input v-model="settings.printProcedure1" label="Procedure 1" />
          <text-input v-model="settings.printProcedure2" label="Procedure 2" />
          <TagInput
            label="Priorities to Print"
            v-model="settings.priorities"
            valueExpr="displayName"
            :items="priorities"
          />
          <div class="border p-2 mx-1 mt-2">
            <div class="d-flex justify-content-between align-items-end">
              <h4>Protocols to Print</h4>
              <AddButton @click="addProtocolToPrint" />
            </div>
            <div
              class="row"
              v-for="protocol of settings.printProtocols"
              v-bind:key="protocol.protocolPrintId"
            >
              <SelectInput
                class="col-5"
                label="Protocol"
                v-model="protocol.protocolPrintId"
                :items="protocolOptions"
                displayExpr="macroName"
                valueExpr="macroId"
              />
              <TextInput class="col-5" label="Display Text" v-model="protocol.protocolPrintText" />
              <IconButton
                v-tooltip.left="'Remove protocol.'"
                @click="removeProtocolToPrint(protocol)"
                class="m-auto text-danger pointer p-0 col-2"
                :icon="'trash-alt'"
              />
            </div>
          </div>
          <button class="btn btn-primary my-1" @click="isCassetteTemplatePopupOpen = true">
            Edit Cassette String Template
          </button>
        </div>
      </div>
      <div class="border p-2 mt-2">
        <h3>Slides</h3>
        <text-input v-model="settings.slidePrinterName" label="Slide Printer Name" />
        <div v-show="settings.slidePrinterName">
          <select-input
            v-model="settings.slidesAtAccessioning"
            :items="booleanOptions"
            label="Print Slides at Accessioning"
          />
          <!-- Unclear if these are actually needed -->
          <!-- <text-input
            v-model="slideTrayOptions"
            label="Slide Tray Options"
            @change="setDefaultSlideTray"
          />
          <select-input
            v-model="settings.defaultSlideTray"
            :items="defaultSlideTrayOptions"
            label="Default Slide Tray"
            valueExpr="displayName"
          /> -->
          <button class="btn btn-primary my-1" @click="isSlideTemplatePopupOpen = true">
            Edit Slide String Template
          </button>
        </div>
      </div>

      <!-- <Checkbox
      label="Denote Single Specimen"
      v-model="settings.denoteSingleSpecimen"
      name="denoteSingleSpecimen"
      id="denoteSingleSpecimen"
      />
      <Checkbox
      label="Denote Single Block"
      v-model="settings.denoteSingleBlock"
      name="denoteSingleBlock"
      id="denoteSingleBlock"
      /> -->
      <button class="btn btn-danger mt-2" @click="handleDisableClick">
        Disable File Drop Printing
      </button>
      <Modal :status="isCassetteTemplatePopupOpen" @close="isCassetteTemplatePopupOpen = false">
        <FileDropTemplatePopup
          templateName="Cassette"
          :originalString="settings.cassetteStringTemplate"
          :trayOptions="settings.trayOptions"
          :defaultTray="settings.defaultTray"
          :printerName="settings.printerName"
          :settings="settings"
          @close="isCassetteTemplatePopupOpen = false"
          @submit="value => updateTemplate('cassetteStringTemplate', value)"
        />
      </Modal>
      <Modal :status="isSlideTemplatePopupOpen" @close="isSlideTemplatePopupOpen = false">
        <FileDropTemplatePopup
          templateName="Slide"
          :originalString="settings.slideStringTemplate"
          :trayOptions="settings.slideTrayOptions"
          :defaultTray="settings.defaultSlideTray"
          :printerName="settings.slidePrinterName"
          :settings="settings"
          @close="isSlideTemplatePopupOpen = false"
          @submit="value => updateTemplate('slideStringTemplate', value)"
        />
      </Modal>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import SelectInput from "../common/SelectInput.vue";
import TagInput from "../common/TagInput.vue";
import TextInput from "../common/TextInput.vue";
import { MacrosApi } from "@/services";
import { MacroTypeEnum } from "@/modules/enums";
import AddButton from "../common/AddButton.vue";
import IconButton from "../common/IconButton.vue";
import Modal from "../common/Modal.vue";
import FileDropTemplatePopup from "../common/FileDropTemplatePopup.vue";
// import Checkbox from "../common/Checkbox.vue";
export default {
  components: {
    TextInput,
    SelectInput,
    TagInput,
    AddButton,
    IconButton,
    Modal,
    FileDropTemplatePopup
  },
  props: ["value"],
  data() {
    return {
      settings: {
        printerName: "",
        url: "",
        atAccessioning: 0,
        trayOptions: [],
        defaultTray: "",
        printProcedure1: "",
        printProcedure2: "",
        priorities: [],
        printProtocols: [],
        cassetteStringTemplate: "",
        sliePrinterName: "",
        slidesAtAccessioning: 0,
        slideTrayOptions: [],
        defaultSlideTray: "",
        slideStringTemplate: ""
      },
      booleanOptions: [
        { displayName: "Yes", id: 1 },
        { displayName: "No", id: 0 }
      ],
      showSettings: false,
      shouldDisable: false,
      protocolOptions: [],
      isCassetteTemplatePopupOpen: false,
      isSlideTemplatePopupOpen: false
    };
  },
  mounted() {
    if (this.value) {
      this.settings = { ...this.settings, ...JSON.parse(this.value) };
      if (Object.keys(this.settings).length) {
        this.showSettings = true;
      }
      if (this.settings?.protocolPrintId && !this.settings?.printProtocols?.length) {
        this.settings.printProtocols.push({
          protocolPrintId: this.settings.protocolPrintId,
          protocolPrintText: this.settings.protocolPrintText
        });
        delete this.settings.protocolPrintId;
        delete this.settings.protocolPrintText;
      }
    }
    this.$store.dispatch("dropdowns/getPriorities");
    MacrosApi.getMacroListByType(MacroTypeEnum.Protocol).then(({ data }) => {
      this.protocolOptions = data;
    });
  },
  computed: {
    ...mapState({
      priorities: state => state.dropdowns.priorities
    }),
    trayOptions: {
      get: function () {
        return this.settings?.trayOptions
          ? this.settings?.trayOptions.map(e => e.displayName).join(",")
          : "";
      },
      set: function (value) {
        if (this.settings?.trayOptions) {
          this.settings.trayOptions = value.split(",").map(e => {
            return { displayName: e };
          });
        } else {
          this.trayOptions = [];
        }
      }
    },
    slideTrayOptions: {
      get: function () {
        return this.settings?.slideTrayOptions
          ? this.settings?.slideTrayOptions.map(e => e.displayName).join(",")
          : "";
      },
      set: function (value) {
        if (this.settings?.slideTrayOptions) {
          this.settings.slideTrayOptions = value.split(",").map(e => {
            return { displayName: e };
          });
        } else {
          this.slideTrayOptions = [];
        }
      }
    },
    defaultTrayOptions() {
      if (this.trayOptions?.length) {
        return this.trayOptions.split(",").map(e => {
          return { displayName: e };
        });
      }
      return [];
    },
    defaultSlideTrayOptions() {
      return this.slideTrayOptions.split(",").map(e => {
        return { displayName: e };
      });
    }
  },
  methods: {
    enableShowSettings() {
      if (!this.settings) {
        this.settings = {
          printerName: "",
          url: "",
          atAccessioning: 0,
          trayOptions: []
        };
      }
      this.showSettings = true;
    },
    setDefaultTray() {
      if (this.trayOptions?.length) {
        const defaultTrayArray = this.defaultTrayOptions.map(e => e.displayName);
        if (
          (!this.settings.defaultTray && defaultTrayArray.length) ||
          !defaultTrayArray.includes(this.settings.defaultTray)
        ) {
          this.settings.defaultTray = defaultTrayArray[0];
        }
      } else {
        this.settings.defaultTray = null;
      }
    },
    setDefaultSlideTray() {
      const defaultTrayArray = this.defaultSlideTrayOptions.map(e => e.displayName);
      if (
        (!this.settings.defaultSlideTray && defaultTrayArray.length) ||
        !defaultTrayArray.includes(this.settings.defaultSlideTray)
      ) {
        this.settings.defaultSlideTray = defaultTrayArray[0];
      }
    },
    async handleDisableClick() {
      const confirm = await window.confirm(
        "Are you sure you want to disable this lab's File Drop settings? All settings will be lost once this action is completed."
      );
      if (confirm) {
        this.$emit("changeJSON", null);
        this.shouldDisable = true;
      }
    },
    addProtocolToPrint() {
      if (!this.settings.printProtocols.find(e => e.protocolPrintId === null)) {
        this.settings.printProtocols.push({ protocolPrintId: null, protocolPrintText: "" });
      }
    },
    removeProtocolToPrint(protocol) {
      this.settings.printProtocols = this.settings.printProtocols.filter(
        e => e.protocolPrintId !== protocol.protocolPrintId
      );
    },
    updateTemplate(property, value) {
      this.settings[property] = value;
    }
  },
  watch: {
    settings: {
      immediate: true,
      deep: true,
      handler(nv) {
        this.$emit("changeJSON", JSON.stringify(nv));
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
